import React, { useEffect, useState, useRef } from 'react';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, IconButton, Box, Typography, TextField, Paper, Pagination } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '../../models';
import { userApi } from '../../services/api';

const PAGE_SIZE = 20;

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
  const navigate = useNavigate();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchUsers = async (pageNum = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(pageNum));
      if (searchTerm) params.append('search', searchTerm);
      params.append('page_size', String(PAGE_SIZE));
      const res = await userApi.getUsers(params.toString());
      setUsers(res.results);
      setCount(res.count);
    } catch (e) {
      setUsers([]);
      setCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [search]);

  useEffect(() => {
    fetchUsers(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  useEffect(() => {
    setPage(paginationModel.page + 1);
  }, [paginationModel.page]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userApi.deleteUser(id);
        fetchUsers(page, search);
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 300 },
    { field: 'username', headerName: 'Username (Phone)', width: 180 },
    { field: 'name', headerName: 'Name', width: 200, valueGetter: (value, row) => `${row.first_name} ${row.last_name}` },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={params.value ? 'success.main' : 'error.main'}>
          {params.value ? 'Active' : 'Inactive'}
        </Typography>
      ),
    },
    {
      field: 'date_joined',
      headerName: 'Joined',
      width: 180,
      valueFormatter: (value) => new Date(value).toLocaleString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => navigate(`/users/${params.row.id}`)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => navigate(`/users/${params.row.id}/edit`)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: 700, width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">User Management</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/users/new')}
        >
          Add New User
        </Button>
      </Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Search by name, username, or email"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          fullWidth
        />
      </Paper>
      <DataGrid
        rows={users}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        rowCount={count}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
      />
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(count / PAGE_SIZE)}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default UserList;